import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { User, Lock, ArrowRight, CheckCircle2 } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
export function LoginPage() {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [loginSuccess, setLoginSuccess] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const handleLogin = (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    // Simulate login validation
    setTimeout(() => {
      setIsLoading(false);
      setLoginSuccess(true);
      // Show success animation then navigate
      setTimeout(() => {
        navigate('/');
      }, 1200);
    }, 1000);
  };
  return <div className="min-h-screen flex items-center justify-center p-4 relative overflow-hidden" style={{
    background: 'linear-gradient(135deg, #395DA8 0%, #31295f 100%)'
  }} data-id="element-2283">
      {/* Decorative Celeste Pittogramma - Large background element */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none" data-id="element-2284">
        {/* Main pittogramma - positioned to the right */}
        <motion.img initial={{
        opacity: 0,
        x: 100
      }} animate={{
        opacity: 0.1,
        x: 0
      }} transition={{
        duration: 1,
        ease: 'easeOut'
      }} src="https://cdn.magicpatterns.com/uploads/15hffbVjBeJ8Re3zEjyeaK/icona_celeste.svg" alt="" className="absolute -right-32 top-1/2 -translate-y-1/2 w-[600px] h-[600px]" aria-hidden="true" data-id="element-2285" />

        {/* Secondary smaller pittogramma - top left */}
        <motion.img initial={{
        opacity: 0,
        rotate: 0
      }} animate={{
        opacity: 0.05,
        rotate: 45
      }} transition={{
        duration: 1.2,
        ease: 'easeOut',
        delay: 0.2
      }} src="https://cdn.magicpatterns.com/uploads/15hffbVjBeJ8Re3zEjyeaK/icona_celeste.svg" alt="" className="absolute -left-20 -top-20 w-[300px] h-[300px]" aria-hidden="true" data-id="element-2286" />

        {/* Subtle gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-br from-transparent via-transparent to-black/10" data-id="element-2287"></div>
      </div>

      {/* Login Card */}
      <motion.div initial={{
      opacity: 0,
      y: 20,
      scale: 0.98
    }} animate={{
      opacity: 1,
      y: 0,
      scale: 1
    }} transition={{
      duration: 0.5,
      ease: 'easeOut'
    }} className="w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden relative z-10" data-id="element-2288">
        <div className="p-8 sm:p-10" data-id="element-2289">
          {/* SEF Logo */}
          <motion.div initial={{
          opacity: 0,
          y: -10
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5,
          delay: 0.2
        }} className="flex flex-col items-center mb-10" data-id="element-2290">
            <img src="https://cdn.magicpatterns.com/uploads/1KdRBrYowz6fjdh8tvZpwA/logo_sef_crm.svg" alt="SEF" className="h-16 w-auto mb-2" data-id="element-2291" />
          </motion.div>

          <AnimatePresence mode="wait" data-id="element-2292">
            {loginSuccess ?
          // Success State
          <motion.div key="success" initial={{
            opacity: 0,
            scale: 0.8
          }} animate={{
            opacity: 1,
            scale: 1
          }} exit={{
            opacity: 0
          }} transition={{
            duration: 0.3,
            ease: 'easeOut'
          }} className="flex flex-col items-center justify-center py-12" data-id="element-2293">
                <motion.div initial={{
              scale: 0
            }} animate={{
              scale: 1
            }} transition={{
              type: 'spring',
              stiffness: 200,
              damping: 15,
              delay: 0.1
            }} className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mb-4" data-id="element-2294">
                  <motion.div initial={{
                scale: 0
              }} animate={{
                scale: 1
              }} transition={{
                type: 'spring',
                stiffness: 300,
                damping: 20,
                delay: 0.3
              }} data-id="element-2295">
                    <CheckCircle2 className="w-12 h-12 text-green-500" data-id="element-2296" />
                  </motion.div>
                </motion.div>
                <motion.p initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: 0.4
            }} className="text-lg font-semibold text-slate-800" data-id="element-2297">
                  Accesso effettuato
                </motion.p>
                <motion.p initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} transition={{
              delay: 0.5
            }} className="text-sm text-slate-500 mt-1" data-id="element-2298">
                  Reindirizzamento in corso...
                </motion.p>
              </motion.div> :
          // Login Form
          <motion.form key="form" initial={{
            opacity: 1
          }} exit={{
            opacity: 0,
            y: -20
          }} transition={{
            duration: 0.2
          }} onSubmit={handleLogin} className="space-y-6" data-id="element-2299">
                <motion.div initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.4,
              delay: 0.3
            }} className="space-y-4" data-id="element-2300">
                  <Input label="Email" placeholder="nome@azienda.it" type="email" value={email} onChange={e => setEmail(e.target.value)} startContent={<User className="h-4 w-4" data-id="element-2302" />} required data-id="element-2301" />
                  <div className="space-y-1" data-id="element-2303">
                    <Input label="Password" placeholder="••••••••" type="password" value={password} onChange={e => setPassword(e.target.value)} startContent={<Lock className="h-4 w-4" data-id="element-2305" />} required data-id="element-2304" />
                    <div className="flex justify-end" data-id="element-2306">
                      <a href="#" className="text-xs text-[#395DA8] hover:text-[#31295f] font-medium transition-colors" data-id="element-2307">
                        Password dimenticata?
                      </a>
                    </div>
                  </div>
                </motion.div>

                <motion.div initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.4,
              delay: 0.4
            }} className="flex items-center" data-id="element-2308">
                  <input id="remember-me" name="remember-me" type="checkbox" className="h-4 w-4 text-[#395DA8] focus:ring-[#395DA8] border-gray-300 rounded cursor-pointer" data-id="element-2309" />
                  <label htmlFor="remember-me" className="ml-2 block text-sm text-slate-600 cursor-pointer" data-id="element-2310">
                    Ricordami
                  </label>
                </motion.div>

                <motion.div initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.4,
              delay: 0.5
            }} data-id="element-2311">
                  <Button type="submit" fullWidth size="lg" isLoading={isLoading} endContent={!isLoading && <ArrowRight className="h-4 w-4" data-id="element-2313" />} className="text-base font-semibold bg-[#395DA8] hover:bg-[#31295f] transition-colors" data-id="element-2312">
                    Accedi
                  </Button>
                </motion.div>
              </motion.form>}
          </AnimatePresence>
        </div>

        {/* Footer with copyright */}
        <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        duration: 0.5,
        delay: 0.6
      }} className="bg-slate-50 px-8 py-4 border-t border-slate-100 text-center" data-id="element-2314">
          <p className="text-xs text-slate-500" data-id="element-2315">
            © 2026 - SEF - Via Calamandrei 185 - 52100 Arezzo, AR
          </p>
        </motion.div>
      </motion.div>
    </div>;
}